import React, { useCallback, useEffect, useState } from 'react';
import { GameState, Situation } from '../../../../../services/api';
import ActionButton from '../../../../Common/ActionButton';
import { useGameActions } from '../../../../../hooks/useGameActions';
import SituationPanel from '../../../../Common/SituationPanel';
import { useTranslation } from 'react-i18next';

interface Props {
  gameState: GameState;
  playerId: string;
  onStateUpdate: (newState: GameState) => void;
}

const RandomSituationSelection: React.FC<Props> = ({gameState, playerId, onStateUpdate}) => {
  const { advanceGame, getRandomSituations, setGameSituation, isLoading } = useGameActions({
      gameCode: gameState.gameCode,
      playerId,
      onStateUpdate
    });
  
  const [situations, setSituations] = useState<Situation[]>([]);
  const [selectedSituationId, setSelectedSituationId] = useState<string>();

  const { t } = useTranslation();

  const loadSituations = async () => {
    try {
      setSelectedSituationId(undefined)
      const newSituations = await getRandomSituations();
      setSituations(newSituations);
    } catch (err) {
      console.error('Failed to load situations:', err);
    }
  };

  const handleSelectSituation = async () => {
    if (!selectedSituationId) return
    try {
      await setGameSituation(selectedSituationId);
      advanceGame()
    } catch (err) {
      console.error('Failed to select topic:', err);
    }
  };

  useEffect(() => {
    if (gameState.state === 'roleAssignment') {
      loadSituations();
    }
  }, [gameState.state]);

  const situationPanelClassNames = useCallback((situationId: string) => {
    return selectedSituationId === situationId
                ? 'border-blue-500 bg-blue-50 border-2'
                : 'border-gray-200 bg-gray-50 border-2 hover:border-gray-300 hover:bg-gray-100'
  }, [selectedSituationId])

  return (
    <div className="w-full flex-1 flex flex-col gap-8">
      <div className="flex-1 flex flex-col gap-4">
        {situations.map((situation) => (
          <SituationPanel key={situation.id} 
            onClick={() => setSelectedSituationId(situation.id)} 
            situation={situation} 
            className={situationPanelClassNames(situation.id)}/>
        ))}
      </div>

      <div className="w-full flex gap-3 justify-center">
        <ActionButton
          onClick={loadSituations}
          loading={isLoading}
          variant="secondary"
          size="md"
          fullWidth={true}
          disabled={isLoading}
        >
          {t('common.showMore')}
        </ActionButton>

        <ActionButton
          onClick={handleSelectSituation}
          loading={isLoading}
          variant="primary"
          size="md"
          fullWidth={true}
          disabled={!selectedSituationId || isLoading}
        >
          {t('game.pageTitle.selectSituation')}
        </ActionButton>
      </div>
    </div>
  );
};

export default RandomSituationSelection;